import { type DragEndEvent } from "@dnd-kit/core";
import { ArrowRight, Plus, Sparkles } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Constants } from "src/constant";
import { actorFromAuth } from "src/lib/activityService";
import {
  buildCarouselPublicationsZipBlob,
  downloadBlob,
} from "src/lib/carouselPublicationExport";
import { auth } from "src/lib/firebase_config";
import {
  computeChangeSummary,
  computePublishStatus,
  draftByIdFromList,
  liveByIdFromList,
  resolveEditingItem,
} from "src/lib/happeningNowPanelLogic";
import { canManageService, canReadService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { useCarouselPublicationsStore } from "src/store/carouselPublicationsStore";
import { useHappeningNowPanelStore } from "src/store/happeningNowPanelStore";
import { useShallow } from "zustand/react/shallow";
import { EditHappeningNowDialog } from "../components/HappeningNow/EditHappeningNowDialog";
import { HappeningNowLocked } from "../components/HappeningNow/HappeningNowLocked";
import { LiveCarouselSection } from "../components/HappeningNow/LiveCarouselSection";
import { PastCarouselsPanel } from "../components/HappeningNow/PastCarouselsPanel";
import { PublicationPreviewDialog } from "../components/HappeningNow/PublicationPreviewDialog";
import { RepublishDialog } from "../components/HappeningNow/RepublishDialog";

const teal = { primary: "#14B8A6", shadow: "#0F766E" } as const;

const labelClass =
  "flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]";

/**
 * Monitor page (`/happening-now`): what's live now + publication history, plus
 * a banner into the focused build workspace (`/happening-now/compose`). Authoring
 * (add card, working draft, publish) lives on that separate page so this screen
 * stays a clean at-a-glance view.
 */
const HappeningNowPanel: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const actor = actorFromAuth(user, userProfile);
  // Authoring on this page (create/continue draft, reorder + pause live cards,
  // publication history actions) all require MANAGE at the Firestore-rules
  // level — WRITE grants nothing actionable here — so gate the UI on manage to
  // avoid surfacing controls that would bounce or be rejected. Read-level users
  // get the live carousel as a view-only "what's live now" screen.
  const canManageHappeningNow =
    !!userProfile &&
    canManageService(userProfile.role, ServiceDomain.HappeningNow);
  const {
    items,
    liveItems,
    loading,
    error,
    success,
    liveBusyId,
    edit,
    editError,
    editSaving,
    editDeleting,
    previewPub,
    republishTarget,
    republishSubmitting,
    republishError,
    pubBusyId,
    setEditForm,
    clearSuccess,
    load,
    openEditFromLive,
    closeEdit,
    saveEdit,
    deleteEdit,
    toggleLiveActive,
    onLiveDragEnd,
    loadPublicationPreview,
    closePublicationPreview,
    openRepublish,
    closeRepublish,
    confirmRepublish,
    togglePublicationStar,
    deletePublication,
  } = useHappeningNowPanelStore(
    useShallow((s) => ({
      items: s.items,
      liveItems: s.liveItems,
      loading: s.loading,
      error: s.error,
      success: s.success,
      liveBusyId: s.liveBusyId,
      edit: s.edit,
      editError: s.editError,
      editSaving: s.editSaving,
      editDeleting: s.editDeleting,
      previewPub: s.previewPub,
      republishTarget: s.republishTarget,
      republishSubmitting: s.republishSubmitting,
      republishError: s.republishError,
      pubBusyId: s.pubBusyId,
      setEditForm: s.setEditForm,
      clearSuccess: s.clearSuccess,
      load: s.load,
      openEditFromLive: s.openEditFromLive,
      closeEdit: s.closeEdit,
      saveEdit: s.saveEdit,
      deleteEdit: s.deleteEdit,
      toggleLiveActive: s.toggleLiveActive,
      onLiveDragEnd: s.onLiveDragEnd,
      loadPublicationPreview: s.loadPublicationPreview,
      closePublicationPreview: s.closePublicationPreview,
      openRepublish: s.openRepublish,
      closeRepublish: s.closeRepublish,
      confirmRepublish: s.confirmRepublish,
      togglePublicationStar: s.togglePublicationStar,
      deletePublication: s.deletePublication,
    })),
  );

  const {
    publications,
    loading: pubLoading,
    starredOnly,
    load: loadPublications,
    toggleStarredOnly,
  } = useCarouselPublicationsStore(
    useShallow((s) => ({
      publications: s.publications,
      loading: s.loading,
      starredOnly: s.starredOnly,
      load: s.load,
      toggleStarredOnly: s.toggleStarredOnly,
    })),
  );

  const [pubExporting, setPubExporting] = useState(false);
  const [pubExportError, setPubExportError] = useState<string | null>(null);

  const handleExportPublications = useCallback(async () => {
    setPubExportError(null);
    if (publications.length === 0) return;
    setPubExporting(true);
    try {
      // Attach the caller's Firebase ID token so the export can fetch images
      // via the Storage REST API (works cross-origin once CORS is configured).
      let idToken: string | null = null;
      try {
        idToken = (await auth.currentUser?.getIdToken()) ?? null;
      } catch {
        // non-fatal — export will still try other strategies
      }
      const blob = await buildCarouselPublicationsZipBlob(
        publications,
        idToken,
      );
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(blob, `happening-now-publications-${stamp}.zip`);
    } catch (e: unknown) {
      setPubExportError(
        e instanceof Error ? e.message : "Could not export publications.",
      );
    } finally {
      setPubExporting(false);
    }
  }, [publications]);

  useEffect(() => {
    void load();
    // Publication history is only rendered for manage-level users; skip the
    // fetch entirely for everyone else.
    if (canManageHappeningNow) void loadPublications();
  }, [load, loadPublications, canManageHappeningNow]);

  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => clearSuccess(), 5000);
    return () => window.clearTimeout(id);
  }, [success, clearSuccess]);

  const liveById = useMemo(() => liveByIdFromList(liveItems), [liveItems]);
  const draftById = useMemo(() => draftByIdFromList(items), [items]);

  const publishStatus = useMemo(
    () => computePublishStatus(items, liveItems),
    [items, liveItems],
  );
  const changeSummary = useMemo(
    () => computeChangeSummary(items, liveItems, publishStatus),
    [items, liveItems, publishStatus],
  );

  const editingItem = useMemo(
    () => resolveEditingItem(edit, items, liveById),
    [edit, items, liveById],
  );
  const editingLiveItem = useMemo(
    () => (edit ? liveById.get(edit.id) : null),
    [edit, liveById],
  );

  const handleLiveDragEndCb = useCallback(
    (event: DragEndEvent) => {
      void onLiveDragEnd(event, actor);
    },
    [actor, onLiveDragEnd],
  );

  const goToCompose = () => navigate(Constants.HAPPENING_NOW_COMPOSE_ROUTE);

  if (
    !userProfile ||
    !canReadService(userProfile.role, ServiceDomain.HappeningNow)
  ) {
    return <HappeningNowLocked />;
  }

  const draftCount = items.length;

  return (
    <div
      className="mx-auto max-w-3xl pb-10 pt-2 sm:max-w-4xl sm:pt-4"
      style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
    >
      <header className="mb-6 text-center sm:mb-8">
        <h1 className="mb-2 flex items-center justify-center gap-2 text-3xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-4xl">
          <span
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl shadow-[0_4px_0_#0F766E] sm:h-14 sm:w-14"
            style={{ backgroundColor: teal.primary }}
          >
            <Sparkles
              className="h-7 w-7 text-white sm:h-8 sm:w-8"
              strokeWidth={2.5}
            />
          </span>
          <span className="max-w-[14ch] leading-tight sm:max-w-none">
            {Constants.HAPPENING_NOW_TITLE}
          </span>
        </h1>
        {canManageHappeningNow && (
          <p className="mx-auto max-w-md text-base font-bold text-white/80 sm:text-lg">
            {Constants.HAPPENING_NOW_DESCRIPTION}
          </p>
        )}
      </header>

      {/* Entry point into the build workspace. When a draft is in progress we
          surface it here so pending, unpublished work is never hidden. */}
      <div className="mb-6">
        {draftCount > 0 && canManageHappeningNow ? (
          <button
            type="button"
            onClick={goToCompose}
            className="flex w-full items-center justify-between gap-3 rounded-2xl border-2 border-[#F59E0B] bg-[#FEF3C7] p-4 text-left shadow-[0_4px_0_#FBBF24] transition-all hover:bg-[#FDE9A9] active:translate-y-0.5"
          >
            <div className="min-w-0">
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-[#B45309]">
                Draft in progress · not published
              </p>
              <p className="truncate text-base font-extrabold text-[#78350F]">
                {draftCount} {draftCount === 1 ? "card" : "cards"}
                {publishStatus === "unpublished_changes" && changeSummary
                  ? ` · ${changeSummary}`
                  : ""}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#F59E0B] px-4 py-2 text-sm font-extrabold text-white shadow-[0_3px_0_#B45309]">
              Continue <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </button>
        ) : (
          canManageHappeningNow && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={goToCompose}
                className="flex w-fit items-center justify-center gap-2 rounded-2xl border-2 border-[#0F766E] bg-[#14B8A6] px-6 py-4 text-base font-extrabold text-white shadow-[0_4px_0_#0F766E] transition-all hover:brightness-105 active:translate-y-0.5"
              >
                <Plus className="h-5 w-5" aria-hidden /> Create New Carousel
              </button>
            </div>
          )
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border-2 border-[#EF4444] bg-[#FEE2E2] p-4">
          <p className="text-center text-sm font-extrabold text-[#B91C1C]">
            {error}
          </p>
        </div>
      )}
      {success && (
        <div className="mb-6 rounded-2xl border-2 border-[#22C55E] bg-[#DCFCE7] p-4">
          <p className="text-center text-sm font-extrabold text-[#166534]">
            {success}
          </p>
        </div>
      )}

      {edit && editingItem && (
        <EditHappeningNowDialog
          item={editingItem}
          state={edit}
          setState={setEditForm}
          saving={editSaving}
          deleting={editDeleting}
          error={editError}
          labelClass={labelClass}
          liveClickCount={editingLiveItem?.clickCount ?? 0}
          liveLastClickedAt={
            editingLiveItem?.lastClickedAt
              ? editingLiveItem.lastClickedAt.toDate()
              : null
          }
          onClose={closeEdit}
          onSubmit={(file) => {
            if (!user?.uid) return Promise.resolve();
            return saveEdit(file, user.uid, actor);
          }}
          onDelete={() => deleteEdit(actor)}
        />
      )}

      {previewPub && (
        <PublicationPreviewDialog
          publication={previewPub}
          onClose={closePublicationPreview}
        />
      )}

      {republishTarget && (
        <RepublishDialog
          publication={republishTarget}
          currentDraftCount={items.length}
          submitting={republishSubmitting}
          error={republishError}
          onClose={() => {
            if (!republishSubmitting) closeRepublish();
          }}
          onConfirm={async (params) => {
            if (!user?.uid) return;
            await confirmRepublish(params, user.uid, actor);
            // On success the store clears the target — send the operator to the
            // build workspace to review the restored draft and publish it.
            if (!useHappeningNowPanelStore.getState().republishTarget) {
              goToCompose();
            }
          }}
        />
      )}

      <section aria-labelledby="live-carousel-heading">
        <LiveCarouselSection
          items={liveItems}
          loading={loading}
          busyId={liveBusyId}
          readOnly={!canManageHappeningNow}
          draftById={draftById}
          onEditInDraft={openEditFromLive}
          onToggleActive={(item) => void toggleLiveActive(item, actor)}
          onDragEnd={handleLiveDragEndCb}
        />
      </section>

      {canManageHappeningNow && (
        <PastCarouselsPanel
          publications={publications}
          loading={pubLoading}
          starredOnly={starredOnly}
          busyId={pubBusyId}
          exporting={pubExporting}
          exportError={pubExportError}
          onToggleStarredOnly={() => {
            void toggleStarredOnly();
          }}
          onPreview={(pub) => void loadPublicationPreview(pub)}
          onRepublish={(pub) => void openRepublish(pub)}
          onToggleStar={(pub) => {
            if (!user?.uid) return;
            void togglePublicationStar(pub, user.uid, actor);
          }}
          onExport={() => {
            void handleExportPublications();
          }}
          onDelete={(pub) => void deletePublication(pub, actor)}
        />
      )}
    </div>
  );
};

export default HappeningNowPanel;
